import { supabase } from '../../../services/supabase/client'
import type {
  InspectionNode,
  InspectionNodeInsert,
  InspectionNodeUpdate,
  InspectionNodeWithDetails,
} from '../types/inspectionNode'
import { createNodeSchema, updateNodeSchema } from '../types/schemas'
import { offlineStorageService } from '../../sync/services/offlineStorage'
import { buildSyncQueueItem } from '../../sync/services/syncQueue'
import { purgeNodeStorage } from '../../../services/storage/storageCleanup'
import { reportBackgroundSyncFailure } from '../../sync/services/syncErrorReporter'
import { withTimeout, ONLINE_REQUEST_TIMEOUT_MS } from '../../sync/services/withTimeout'
import { v4 as uuidv4 } from 'uuid'

/**
 * Fired (with {jobId} detail) when a background cache refresh finds the
 * server's node list differs from what was cached — views listen and
 * refetch so they aren't one page-load behind the server.
 */
export const NODES_REFRESHED_EVENT = 'arborstack:nodes-refreshed'

/** Order-insensitive fingerprint of a node list for change detection */
const nodeListSignature = (
  nodes: { id: string; updated_at: string | null; photo_count?: number }[]
) =>
  nodes
    .map((node) => `${node.id}:${node.updated_at ?? ''}:${node.photo_count ?? ''}`)
    .sort()
    .join('|')

export class NodeService {
  /**
   * Get all nodes for a job
   * OFFLINE-FIRST APPROACH: Load from IndexedDB immediately, sync in background
   * If IndexedDB is empty and online, wait for sync to complete
   */
  async getNodesByJobId(jobId: string): Promise<InspectionNodeWithDetails[]> {
    // OFFLINE-FIRST: Always load from IndexedDB first for instant response
    const localNodes = await offlineStorageService.getNodesByJobId(jobId)

    // If online, sync with Supabase
    if (navigator.onLine) {
      // If we have local data, sync in background (fire and forget)
      // If IndexedDB is empty, wait for sync to complete (fresh login scenario)
      if (localNodes.length > 0) {
        void this.syncNodesInBackground(jobId)
        return this.mergeLocalPhotoCounts(jobId, localNodes)
      } else {
        // No local data - wait for the Supabase fetch, but never hang on a
        // dead link that navigator.onLine misreports (VPN, captive portal)
        const freshNodes = await withTimeout(
          this.fetchNodesFromSupabase(jobId),
          ONLINE_REQUEST_TIMEOUT_MS,
          'node list fetch'
        ).catch(() => localNodes)
        return this.mergeLocalPhotoCounts(jobId, freshNodes)
      }
    }

    return this.mergeLocalPhotoCounts(jobId, localNodes)
  }

  /**
   * Node records carry the photo list as a server snapshot, which goes stale
   * the moment a photo is captured offline. Fold in the locally stored
   * photos still awaiting their create — and only those: a local copy of an
   * already-synced photo missing from the snapshot means it was deleted
   * from another device, and counting it would inflate the badge forever.
   * Counts only, so blob URLs are skipped (no object-URL churn per read).
   */
  private async mergeLocalPhotoCounts(
    jobId: string,
    nodes: InspectionNodeWithDetails[]
  ): Promise<InspectionNodeWithDetails[]> {
    const localPhotos = await offlineStorageService.getPhotosByJobId(jobId, false)
    if (localPhotos.length === 0) return nodes

    const pendingCreateIds = await offlineStorageService.getPendingPhotoCreateIds()
    const byNode = new Map<string, typeof localPhotos>()
    for (const photo of localPhotos) {
      if (!photo.node_id) continue
      if (!pendingCreateIds.has(photo.id) && !photo.id.startsWith('temp_')) continue
      const list = byNode.get(photo.node_id) ?? []
      list.push(photo)
      byNode.set(photo.node_id, list)
    }

    return nodes.map((node) => {
      const known = node.photos ?? []
      const extra = (byNode.get(node.id) ?? []).filter(
        (photo) => !known.some((existing) => existing.id === photo.id)
      )
      const photos = [...known, ...extra]
      return { ...node, photos, photo_count: photos.length }
    })
  }

  /**
   * The node list with its photos in one round trip. This used to be a
   * photos query per node, so a 50-node job cost 51 requests on every job
   * open and every background sync. PostgREST orders an embedded resource
   * by its own key (`photos.order=`), which supabase-js spells
   * `referencedTable`, so the photo order the gallery relies on survives
   * the collapse.
   */
  private nodesWithPhotosQuery(jobId: string) {
    return supabase
      .from('inspection_nodes')
      .select('*, photos(id, storage_path, thumbnail_path, caption)')
      .eq('job_id', jobId)
      .order('node_number', { ascending: true })
      .order('created_at', { referencedTable: 'photos', ascending: true })
  }

  /**
   * Shape the embedded rows into the details the views read. A node with no
   * photos embeds as `[]`, never null, so the count needs no other guard.
   */
  private toNodesWithDetails(
    rows: Awaited<ReturnType<NodeService['nodesWithPhotosQuery']>>['data']
  ): InspectionNodeWithDetails[] {
    return (rows ?? []).map((row) => {
      const { photos, ...node } = row
      return {
        ...node,
        photo_count: photos?.length ?? 0,
        photos: photos ?? [],
        sync_status: 'synced' as const,
        stored_locally: false,
      }
    })
  }

  /**
   * Fetch nodes directly from Supabase and cache them
   */
  private async fetchNodesFromSupabase(jobId: string): Promise<InspectionNodeWithDetails[]> {
    try {
      const { data, error } = await this.nodesWithPhotosQuery(jobId)

      if (error) throw error

      const nodesWithDetails = this.toNodesWithDetails(data)

      // Update IndexedDB cache
      for (const node of nodesWithDetails) {
        await offlineStorageService.saveNode(node)
      }

      return nodesWithDetails
    } catch (error) {
      console.error('Failed to fetch nodes from Supabase:', error)
      return []
    }
  }

  /**
   * Background sync for nodes
   * Updates IndexedDB cache with fresh data from Supabase
   */
  private async syncNodesInBackground(jobId: string): Promise<void> {
    try {
      // Snapshot the cache first so we can tell whether this refresh
      // actually changed anything
      const [cachedNodes, queueItems] = await Promise.all([
        offlineStorageService.getNodesByJobId(jobId),
        offlineStorageService.getQueueItems(),
      ])
      const pendingNodeIds = new Set(
        queueItems
          .filter((item) => item.entity === 'node' && item.operation === 'create')
          .map((item) => item.entityId)
      )

      const { data, error } = await this.nodesWithPhotosQuery(jobId)

      if (error) throw error

      const nodesWithDetails = this.toNodesWithDetails(data)

      // Prune cached nodes the server no longer has (deleted from another
      // device) — but never a local creation still awaiting its sync
      const serverIds = new Set(nodesWithDetails.map((node) => node.id))
      for (const cached of cachedNodes) {
        if (serverIds.has(cached.id)) continue
        if (cached.id.startsWith('temp_') || pendingNodeIds.has(cached.id)) continue
        await offlineStorageService.deleteNode(cached.id)
      }

      // Update IndexedDB cache
      for (const node of nodesWithDetails) {
        await offlineStorageService.saveNode(node)
      }

      // Notify views when the server list differs from what was cached.
      // Unsynced local nodes are excluded from the comparison — they are
      // never on the server, and counting them would re-fire every pass.
      const cachedComparable = cachedNodes.filter(
        (node) => !node.id.startsWith('temp_') && !pendingNodeIds.has(node.id)
      )
      if (nodeListSignature(nodesWithDetails) !== nodeListSignature(cachedComparable)) {
        window.dispatchEvent(new CustomEvent(NODES_REFRESHED_EVENT, { detail: { jobId } }))
      }
    } catch (error) {
      reportBackgroundSyncFailure('Background node list refresh failed', error)
    }
  }

  /**
   * Get a single node by ID
   * OFFLINE-FIRST APPROACH: Load from IndexedDB immediately, sync in background
   */
  async getNode(id: string): Promise<InspectionNodeWithDetails | null> {
    // OFFLINE-FIRST: Always load from IndexedDB first for instant response
    const localNode = await offlineStorageService.getNode(id)

    // If online, sync in background and update cache
    if (navigator.onLine) {
      // Fire and forget - don't await
      void this.syncNodeInBackground(id)
    }

    return localNode
  }

  /**
   * Background sync for single node
   * Updates IndexedDB cache with fresh data from Supabase
   */
  private async syncNodeInBackground(id: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('inspection_nodes')
        .select('*, photos(id, storage_path, thumbnail_path, caption)')
        .eq('id', id)
        .order('created_at', { referencedTable: 'photos', ascending: true })
        .single()

      if (error) throw error
      if (!data) return

      const { photos, ...node } = data
      const enrichedNode = {
        ...node,
        photo_count: photos?.length ?? 0,
        photos: photos ?? [],
      }

      // Update IndexedDB cache
      await offlineStorageService.saveNode(enrichedNode)
    } catch (error) {
      reportBackgroundSyncFailure('Background node refresh failed', error)
    }
  }

  /**
   * Insert a node using a server-assigned node number (REL-03).
   * The id is client-generated so a replay of a lost-response insert
   * collides on the primary key instead of duplicating the node.
   */
  private async insertNodeWithAssignedNumber(
    validated: ReturnType<typeof createNodeSchema.parse>,
    nodeId: string
  ) {
    const { data: nodeNumber, error: numberError } = await supabase.rpc('assign_node_number', {
      p_job_id: validated.job_id,
    })

    if (numberError) {
      return { data: null, error: numberError }
    }

    return supabase
      .from('inspection_nodes')
      .insert({
        ...validated,
        id: nodeId,
        node_number: nodeNumber,
      })
      .select()
      .single()
  }

  /**
   * Create a new inspection node
   */
  async createNode(nodeData: InspectionNodeInsert): Promise<InspectionNode> {
    // Validate data
    const validated = createNodeSchema.parse(nodeData)

    // One id for every path — online insert, offline record, queued replay.
    // If the online attempt times out AFTER the server accepted it, the
    // queued create hits a primary-key conflict instead of making a twin.
    const nodeId = uuidv4()

    try {
      // If online, create in Supabase
      if (navigator.onLine) {
        // Numbers are assigned server-side and enforced by
        // UNIQUE (job_id, node_number); a concurrent client can win the
        // number we were assigned, so retry once on unique violation (23505)
        let { data, error } = await withTimeout(
          this.insertNodeWithAssignedNumber(validated, nodeId),
          ONLINE_REQUEST_TIMEOUT_MS,
          'node create'
        )
        if (error?.code === '23505') {
          ;({ data, error } = await withTimeout(
            this.insertNodeWithAssignedNumber(validated, nodeId),
            ONLINE_REQUEST_TIMEOUT_MS,
            'node create retry'
          ))
        }

        if (error) throw error
        if (!data) throw new Error('Node insert returned no row')

        // Store in IndexedDB immediately for faster access
        await offlineStorageService.saveNode(data)

        return data
      }
    } catch (_error) {
      // Silent fail and fallback to offline mode
    }

    // If offline or online creation failed, create locally under the SAME id

    // Get next node number from local storage
    const existingNodes = await offlineStorageService.getNodesByJobId(validated.job_id)
    const nextNodeNumber =
      existingNodes.length > 0 ? Math.max(...existingNodes.map((n) => n.node_number || 0)) + 1 : 1

    const localNode: InspectionNode = {
      id: nodeId,
      job_id: validated.job_id,
      node_number: nextNodeNumber,
      node_tag: validated.node_tag || null,
      gps_latitude: validated.gps_latitude,
      gps_longitude: validated.gps_longitude,
      gps_accuracy: validated.gps_accuracy,
      elevation: null,
      common_name: validated.common_name || null,
      botanical_name: validated.botanical_name || null,
      dbh_cm: validated.dbh_cm || null,
      drf_cm: validated.drf_cm || null,
      height_m: validated.height_m || null,
      crown_spread_m: validated.crown_spread_m || null,
      condition_rating: validated.condition_rating || null,
      risk_rating: validated.risk_rating || null,
      health_rating: null,
      structural_rating: null,
      hazard_level: null,
      structural_issues: null,
      health_notes: validated.health_notes || null,
      risk_assessment: validated.risk_assessment || null,
      recommended_action: validated.recommended_action || null,
      notes: validated.notes || null,
      invasive_status: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    // Store in IndexedDB and enqueue for sync in one transaction (REL-09).
    // The payload carries the client id so the queued insert is idempotent.
    await offlineStorageService.saveNodeWithQueueItem(
      localNode,
      buildSyncQueueItem('create', 'node', nodeId, { ...validated, id: nodeId })
    )

    return localNode
  }

  /**
   * Update an existing node
   */
  async updateNode(id: string, updates: InspectionNodeUpdate): Promise<InspectionNode> {
    // Validate data
    const validated = updateNodeSchema.parse(updates)

    try {
      // If online, update in Supabase
      if (navigator.onLine) {
        const { data, error } = await withTimeout(
          supabase.from('inspection_nodes').update(validated).eq('id', id).select().single(),
          ONLINE_REQUEST_TIMEOUT_MS,
          'node update'
        )

        if (error) throw error
        return data
      }
    } catch (error) {
      console.error('Failed to update node online:', error)
    }

    // If offline or online update failed, update locally
    const existingNode = await offlineStorageService.getNode(id)
    if (!existingNode) {
      throw new Error('Tree not found')
    }

    const updatedNode: InspectionNode = {
      ...existingNode,
      ...validated,
      updated_at: new Date().toISOString(),
    }

    // Store in IndexedDB and enqueue for sync in one transaction (REL-09)
    await offlineStorageService.saveNodeWithQueueItem(
      updatedNode,
      buildSyncQueueItem('update', 'node', id, validated)
    )

    return updatedNode
  }

  /**
   * Delete a node
   */
  async deleteNode(id: string): Promise<void> {
    try {
      // If online, delete from Supabase
      if (navigator.onLine) {
        // Photo files before the node row: the rows naming them cascade away
        // with the node, taking the storage keys with them
        await purgeNodeStorage(id)

        const { error } = await withTimeout(
          supabase.from('inspection_nodes').delete().eq('id', id),
          ONLINE_REQUEST_TIMEOUT_MS,
          'node delete'
        )

        if (error) throw error

        // Also delete from IndexedDB if stored locally
        await offlineStorageService.deleteNode(id)
        return
      }
    } catch (error) {
      console.error('Failed to delete node online:', error)
    }

    // If offline, delete locally and enqueue for sync in one transaction (REL-09)
    await offlineStorageService.deleteNodeWithQueueItem(
      id,
      buildSyncQueueItem('delete', 'node', id, { id })
    )
  }

  /**
   * Get next node number for a job
   */
  async getNextNodeNumber(jobId: string): Promise<number> {
    try {
      if (navigator.onLine) {
        const { data, error } = await withTimeout(
          supabase.rpc('assign_node_number', { p_job_id: jobId }),
          ONLINE_REQUEST_TIMEOUT_MS,
          'node number'
        )

        if (error) throw error

        return data
      }
    } catch (error) {
      console.error('Failed to get next node number online:', error)
    }

    // If offline, get from IndexedDB
    const nodes = await offlineStorageService.getNodesByJobId(jobId)
    return nodes.length > 0 ? Math.max(...nodes.map((n) => n.node_number || 0)) + 1 : 1
  }
}

// Export singleton instance
export const nodeService = new NodeService()
